import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '../lib/utils'

interface Column<T> {
  key: string
  title: string
  render?: (item: T) => React.ReactNode
  mobileHidden?: boolean
}

interface MobileTableProps<T> {
  data: T[]
  columns: Column<T>[]
  keyExtractor: (item: T) => string
  title?: string
  emptyState?: React.ReactNode
  onRowClick?: (item: T) => void
  expandable?: boolean
  renderExpanded?: (item: T) => React.ReactNode
}

export function MobileTable<T>({
  data,
  columns,
  keyExtractor,
  title,
  emptyState,
  onRowClick,
  expandable = false,
  renderExpanded,
}: MobileTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleExpanded = (key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(key)) {
      newExpanded.delete(key)
    } else {
      newExpanded.add(key)
    }
    setExpandedRows(newExpanded)
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        {title && (
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">{title}</h3>
          </div>
        )}
        <div className="p-8">
          {emptyState || <p className="text-center text-gray-500">No data available</p>}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      {title && (
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
      )}

      {/* Mobile View - Cards */}
      <div className="md:hidden">
        {data.map((item) => {
          const key = keyExtractor(item)
          const isExpanded = expandedRows.has(key)

          return (
            <div
              key={key}
              className={cn(
                'border-b border-gray-200 last:border-0',
                onRowClick && 'cursor-pointer active:bg-gray-50'
              )}
              onClick={() => onRowClick?.(item)}
            >
              <div className="p-4">
                {/* Primary Column (first column) */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    {columns[0]?.render 
                      ? columns[0].render(item)
                      : <span className="font-medium text-gray-900 truncate">{(item as any)[columns[0]?.key]}</span>}
                  </div>
                  <div className="flex items-center ml-4">
                    {/* Second column as badge/value */}
                    {columns[1] && !columns[1].mobileHidden && (
                      <div className="mr-3">
                        {columns[1].render
                          ? columns[1].render(item)
                          : <span className="text-gray-600">{(item as any)[columns[1].key]}</span>}
                      </div>
                    )}
                    
                    {expandable && (
                      <button
                        onClick={(e) => toggleExpanded(key, e)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Secondary Info Row */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {columns.slice(2).filter(col => !col.mobileHidden).map((column) => (
                    <div key={column.key} className="flex flex-col">
                      <span className="text-xs text-gray-500">{column.title}</span>
                      <span className="text-gray-900 truncate">
                        {column.render
                          ? column.render(item)
                          : (item as any)[column.key]}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Expanded Content */}
                {expandable && isExpanded && renderExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {renderExpanded(item)}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Desktop View - Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((item) => (
              <tr
                key={keyExtractor(item)}
                className={cn(
                  'hover:bg-gray-50',
                  onRowClick && 'cursor-pointer'
                )}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                  >
                    {column.render
                      ? column.render(item)
                      : (item as any)[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Simple card view for very simple data
export function MobileCard<T>({
  data,
  renderCard,
  keyExtractor,
  title,
  emptyState,
}: {
  data: T[]
  renderCard: (item: T) => React.ReactNode
  keyExtractor: (item: T) => string
  title?: string
  emptyState?: React.ReactNode
}) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200">
        {title && (
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">{title}</h3>
          </div>
        )}
        <div className="p-8">
          {emptyState || <p className="text-center text-gray-500">No data available</p>}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 md:space-y-0 md:bg-white md:rounded-xl md:border md:border-gray-200">
      {title && (
        <div className="hidden md:block px-6 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
      )}
      <div className="md:hidden space-y-4">
        {data.map((item) => (
          <div key={keyExtractor(item)} className="bg-white rounded-xl border border-gray-200">
            {renderCard(item)}
          </div>
        ))}
      </div>
      <div className="hidden md:block">
        {data.map((item) => (
          <div key={keyExtractor(item)} className="border-b border-gray-200 last:border-0">
            {renderCard(item)}
          </div>
        ))}
      </div>
    </div>
  )
}
